// MEE 실전 테스트 메인 페이지
// MPT 실전 테스트 구조 기반, Q1~Q6 PDF, A1~A6 답안 입력
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Button, 
  Container, 
  Typography, 
  Box, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  CircularProgress, 
  Alert, 
  Link, 
  Tabs, 
  Tab, 
  Divider, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  Card,
  CardContent,
  Grid,
  Paper,
  Fade,
  LinearProgress
} from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Timer as TimerIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  LocalLibrary as LibraryIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  School as SchoolIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { withAuthRefresh } from '../../utils/auth';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { v4 as uuidv4 } from 'uuid';
import rehypeRaw from 'rehype-raw';
import { Link as RouterLink } from 'react-router-dom';
import OrderedList from '@tiptap/extension-ordered-list';
import Blockquote from '@tiptap/extension-blockquote';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const PdfViewer = ({ url, label }) => {
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const containerRef = useRef(null);
  const [pdfWidth, setPdfWidth] = useState(800);

  useEffect(() => {
    const updatePdfSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        setPdfWidth(Math.min(containerWidth - 16, 1200));
      }
    };
    updatePdfSize();
    window.addEventListener('resize', updatePdfSize);
    return () => window.removeEventListener('resize', updatePdfSize);
  }, []);

  useEffect(() => {
    setPage(1);
    setNumPages(null);
  }, [url]);

  return (
    <Box 
      ref={containerRef} 
      sx={{ 
        width: '100%', 
        height: '100%', 
        minHeight: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'stretch' 
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 2, pt: 2 }}>
        <DescriptionIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
        <Typography variant="h6" fontWeight="bold" color="primary.main">
          {label}
        </Typography>
      </Box>
      {url ? (
        <>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            <Document
              file={url}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={error => {
                console.error('PDF 로딩 에러:', error);
                alert('PDF를 불러올 수 없습니다. (콘솔 에러 참고)');
              }}
            >
              <Page
                pageNumber={page}
                width={pdfWidth}
              />
            </Document>
          </Box>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 2, 
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(0,0,0,0.02)',
              flexShrink: 0
            }}
          >
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => setPage(p => Math.max(1, p - 1))} 
              disabled={page <= 1}
              sx={{ borderRadius: 2 }}
            >
              이전
            </Button>
            <Typography fontWeight="bold" color="primary.main">
              {page} / {numPages || '?'}
            </Typography>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => setPage(p => Math.min(numPages || 1, p + 1))} 
              disabled={!numPages || page >= numPages}
              sx={{ borderRadius: 2 }}
            >
              다음
            </Button>
          </Box>
        </>
      ) : (
        <Box sx={{ 
          flex: 1,
          height: 400, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          color: 'text.secondary',
          flexDirection: 'column',
          gap: 2
        }}>
          <DescriptionIcon sx={{ fontSize: 64, opacity: 0.3 }} />
          <Typography variant="h6" color="text.secondary">
            문제 PDF를 선택하세요
          </Typography>
        </Box>
      )}
    </Box>
  );
};

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const getTimerDuration = (problemCount) => problemCount * 30 * 60;

const RichTextEditor = ({ value, onChange, disabled, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      OrderedList,
      Blockquote,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Underline,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none',
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor && !editor.getText() && placeholder) {
      editor.commands.setContent(`<p class="is-editor-empty" data-placeholder="${placeholder}"></p>`);
    }
  }, [editor, placeholder]);

  if (!editor) {
    return null;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 0.5, 
        p: 0.5,
        borderBottom: '1px solid #e0e3e7',
        background: '#f8f9fa',
        flexShrink: 0,
      }}>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 1 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          H1
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 2 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          H2
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('heading', { level: 3 }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          H3
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          •
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('bold') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBold().run()}
          sx={{ minWidth: 40, px: 1, fontWeight: 'bold' }}
          disabled={disabled}
        >
          B
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('italic') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          sx={{ minWidth: 40, px: 1, fontStyle: 'italic' }}
          disabled={disabled}
        >
          I
        </Button>
        <Button 
          size="small" 
          variant={editor.isActive('underline') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          sx={{ minWidth: 40, px: 1, textDecoration: 'underline' }}
          disabled={disabled}
        >
          U
        </Button>
        <Button
          size="small"
          variant={editor.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          <FormatAlignLeftIcon fontSize="small" />
        </Button>
        <Button
          size="small"
          variant={editor.isActive({ textAlign: 'center' }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          <FormatAlignCenterIcon fontSize="small" />
        </Button>
        <Button
          size="small"
          variant={editor.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          <FormatAlignRightIcon fontSize="small" />
        </Button>
        <Button
          size="small"
          variant={editor.isActive('blockquote') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          "
        </Button>
        <Button
          size="small"
          variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          sx={{ minWidth: 40, px: 1 }}
          disabled={disabled}
        >
          1.
        </Button>
      </Box>
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        p: 1,
        background: '#fff',
        '& .ProseMirror': {
          minHeight: 120,
          height: '100%',
          outline: 'none',
          '& > * + *': {
            marginTop: '0.75em',
          },
          '& h1': {
            fontSize: '1.4em',
            fontWeight: 600,
          },
          '& h2': {
            fontSize: '1.2em',
            fontWeight: 600,
          },
          '& h3': {
            fontSize: '1.1em',
            fontWeight: 600,
          },
          '& ul': {
            padding: '0 1em',
            '& li': {
              margin: '0.2em 0',
            },
          },
          '& p.is-editor-empty:first-child::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: '#adb5bd',
            pointerEvents: 'none',
            height: 0,
          },
        },
      }}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

function splitAnswerContent(str, chunkSize = 900000) {
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let chunks = [];
  let offset = 0;
  while (offset < bytes.length) {
    let end = offset + chunkSize;
    if (end > bytes.length) end = bytes.length;
    let chunk = new TextDecoder().decode(bytes.slice(offset, end));
    while (new TextEncoder().encode(chunk).length > chunkSize) {
      chunk = chunk.slice(0, -1);
    }
    chunks.push(chunk);
    offset += new TextEncoder().encode(chunk).length;
  }
  return chunks;
}

// 월 영문 약어 변환 함수
const monthAbbr = (month) => {
  const MONTHS = { '1': 'Jan', '2': 'Feb', '3': 'Mar', '4': 'Apr', '5': 'May', '6': 'Jun', '7': 'Jul', '8': 'Aug', '9': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec',
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun', '07': 'Jul', '08': 'Aug', '09': 'Sep',
    'January': 'Jan', 'February': 'Feb', 'March': 'Mar', 'April': 'Apr', 'May': 'May', 'June': 'Jun', 'July': 'Jul', 'August': 'Aug', 'September': 'Sep', 'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
  };
  return MONTHS[month] || month;
};

// 한글명 매핑
const ticketTypeLabels = {
  'MEE_AI_6': 'MEE 응시권 (AI 평가, 6문제)',
  'MEE_PROFESSOR_6': 'MEE 응시권 (Professor 평가, 6문제)',
  // 필요시 추가
};

const MeeTestMainPage = () => {
  const [sets, setSets] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [timer, setTimer] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef();
  const [problems, setProblems] = useState([]); // subject별 문제 목록
  const [answers, setAnswers] = useState([]);   // subject별 답안
  const [activeIdx, setActiveIdx] = useState(0);
  const [subjectList, setSubjectList] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [ticketCount, setTicketCount] = useState(0);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [userTickets, setUserTickets] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [usedCount, setUsedCount] = useState(0);
  const [meeTicketList, setMeeTicketList] = useState([]);
  const [selectedMeeTicketType, setSelectedMeeTicketType] = useState('');
  const [answer, setAnswer] = useState('');
  const [hasTicket, setHasTicket] = useState(false); // 응시권 보유 여부 상태 추가
  const [ticketList, setTicketList] = useState([]);
  const [selectedTicketType, setSelectedTicketType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  // 년도/월 목록
  const years = React.useMemo(() => {
    const uniqueYears = [...new Set(sets.map(set => set.year))];
    return uniqueYears.sort((a, b) => b - a);
  }, [sets]);
  const months = React.useMemo(() => {
    if (!selectedYear) return [];
    const uniqueMonths = [...new Set(sets.filter(set => set.year === selectedYear).map(set => set.month))];
    return uniqueMonths.sort((a, b) => a - b);
  }, [sets, selectedYear]);

  // 전체 문제 raw 데이터(년도/월 목록 추출용)
  useEffect(() => {
    async function fetchSets() {
      const { data } = await supabase.from('mee_paper_sets').select('*').order('year', { ascending: false });
      setSets(data || []);
    }
    fetchSets();
  }, []);

  // 과목 목록 불러오기 (최초 1회)
  useEffect(() => {
    async function fetchSubjects() {
      const { data } = await supabase.from('mee_paper_sets').select('subject').order('subject');
      const uniqueSubjects = [...new Set((data || []).map(d => d.subject))];
      setSubjectList(uniqueSubjects);
    }
    fetchSubjects();
  }, []);

  // 과목 선택 핸들러
  const handleSubjectChange = (e) => {
    setSelectedSubject(e.target.value);
    setSelectedYear('');
    setSelectedMonth('');
  };
  // 년도 선택 핸들러
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setSelectedSubject('');
  };
  // 월 선택 핸들러
  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setSelectedSubject('');
  };

  // 문제 불러오기 (과목별 or 년/월별)
  useEffect(() => {
    async function fetchProblems() {
      if (selectedSubject) {
        // 과목별 문제만 불러오기
        const { data: problemsData } = await supabase
          .from('mee_paper_sets')
          .select('*')
          .eq('subject', selectedSubject)
          .order('year', { ascending: true })
          .order('month', { ascending: true });
        const newProblems = (problemsData || []).map(p => {
          let raw = p.q_url || '';
          let finalUrl = raw.startsWith('http')
            ? raw
            : `${supabaseUrl}/storage/v1/object/public/mee-pdfs/${raw.replace(/^\/+/,'').replace(/^mee-pdfs[\\/]+/,'')}`;
          return { ...p, q_url: finalUrl };
        });
        setProblems(newProblems);
        setAnswers(Array(newProblems.length).fill(''));
      } else if (selectedYear && selectedMonth) {
        // 년/월별 문제만 불러오기
        const { data: problemsData } = await supabase
          .from('mee_paper_sets')
          .select('*')
          .eq('year', selectedYear)
          .eq('month', selectedMonth)
          .order('subject');
        const newProblems = (problemsData || []).map(p => {
          let raw = p.q_url || '';
          let finalUrl = raw.startsWith('http')
            ? raw
            : `${supabaseUrl}/storage/v1/object/public/mee-pdfs/${raw.replace(/^\/+/,'').replace(/^mee-pdfs[\\/]+/,'')}`;
          return { ...p, q_url: finalUrl };
        });
        setProblems(newProblems);
        // 답안 불러오기 (로그인 필요)
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) {
          setAnswers(Array((problemsData || []).length).fill(''));
          return;
        }
        const userId = session.user.id;
        const { data: answersData } = await supabase
          .from('mee_answers')
          .select('subject, answer_content, answer_content_extra1, answer_content_extra2')
          .eq('user_id', userId)
          .eq('year', selectedYear)
          .eq('month', selectedMonth);
        const answerMap = {};
        (answersData || []).forEach(a => {
          answerMap[a.subject] = (a.answer_content || '') + (a.answer_content_extra1 || '') + (a.answer_content_extra2 || '');
        });
        setAnswers((problemsData || []).map(p => answerMap[p.subject] || ''));
      } else {
        setProblems([]);
        setAnswers([]);
      }
    }
    fetchProblems();
  }, [selectedSubject, selectedYear, selectedMonth]);

  // 문제 개수에 따라 타이머 재설정 및 자동 시작
  useEffect(() => {
    if (problems.length > 0) {
      setTimer(getTimerDuration(problems.length));
      setTimerActive(true); // 문제 바뀌면 자동 시작
      setIsSubmitted(false);
    } else {
      setTimerActive(false);
    }
  }, [problems.length]);

  // 타이머 카운트다운
  useEffect(() => {
    if (!timerActive) return;
    if (timer <= 0) {
      setTimerActive(false);
      if (!isSubmitted) {
        handleMeeSubmit();
      }
    }
    timerRef.current = setTimeout(() => setTimer(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timer, timerActive]);

  // 답안 입력 핸들러
  const handleAnswerChange = idx => val => {
    setAnswers(prev => prev.map((a, i) => (i === idx ? val : a)));
  };

  // 1. 응시권 보유 개수
  useEffect(() => {
    async function fetchTicket() {
      setTicketLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setTicketCount(0);
        setTicketLoading(false);
        return;
      }
      const { data, error } = await supabase
        .from('user_exam_tickets')
        .select('count')
        .eq('user_id', userId)
        .eq('type', 'MEE_AI_6')
        .single();
      setTicketCount(data?.count || 0);
      setTicketLoading(false);
    }
    fetchTicket();
  }, []);

  // 2. 사용한 횟수 (평가 완료만 카운트, evaluation_type 필요)
  useEffect(() => {
    async function fetchUsedCount() {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const { count: usedCount } = await supabase
        .from('mee_evaluations')
        .select('*', { count: 'exact', head: true })
        .eq('evaluation_type', 'ai')
        .eq('user_id', userId)
        .eq('evaluation_status', '평가완료');
      setUsedCount(usedCount || 0);
    }
    fetchUsedCount();
  }, []);

  // 3. 계산
  const perTicket = 6;
  const total = ticketCount * perTicket;
  const used = usedCount || 0;
  const remain = Math.max(total - used, 0);

  // 4. 표시
  useEffect(() => {
    async function fetchTickets() {
      const { data } = await supabase.from('user_exam_tickets').select('*');
      setUserTickets(data || []);
    }
    fetchTickets();
  }, []);

  useEffect(() => {
    async function fetchTicketTypes() {
      const { data } = await supabase.from('ticket_types').select('*');
      setTicketTypes(data || []);
    }
    fetchTicketTypes();
  }, []);

  // 1. 모든 MEE 응시권/스터디권 통합 조회 (MPT 기준)
  useEffect(() => {
    async function fetchTickets() {
      setTicketLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setTicketList([]);
        setSelectedTicketType('');
        setTicketLoading(false);
        return;
      }
      // 1. 응시권
      const { data: examTickets } = await supabase
        .from('user_exam_tickets')
        .select('type, count')
        .eq('user_id', userId)
        .like('type', 'MEE_%');
      // 2. 스터디권
      const { data: studyTickets } = await supabase
        .from('user_study_tickets')
        .select('type, count')
        .eq('user_id', userId)
        .eq('type', 'STUDY_MEE_1');
      // 3. 통합
      const allTickets = [
        ...(examTickets || []),
        ...(studyTickets || [])
      ];
      setTicketList(allTickets);
      // 자동 선택: 우선순위(응시권 > 스터디권)
      if (allTickets && allTickets.length > 0) {
        setSelectedTicketType(allTickets[0].type);
      } else {
        setSelectedTicketType('');
      }
      setTicketLoading(false);
    }
    fetchTickets();
  }, []);

  // 응시권 체크 함수 추가
  const checkTicket = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) {
      setHasTicket(false);
      return;
    }

    const { data, error } = await supabase
      .from('user_exam_tickets')
      .select('count')
      .eq('user_id', userId)
      .like('type', 'MEE_%')
      .gt('count', 0)
      .single();

    setHasTicket(!!data?.count);
  };

  // 컴포넌트 마운트 시 응시권 체크
  useEffect(() => {
    checkTicket();
  }, []);

  // 2. 답안 제출
  const handleMeeSubmit = async () => {
    if (!selectedMeeTicketType || !answer.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);

      await withAuthRefresh(async (user) => {
        const ticket = meeTicketList.find(t => t.type === selectedMeeTicketType);
        if (!ticket || ticket.count < 1) {
          throw new Error('선택한 응시권이 없습니다. 구매 후 이용해 주세요.');
        }

        const insertPayloads = problems.map((problem, idx) => ({
          submission_group_id: uuidv4(),
          subject: problem.subject,
          answer_content: answers[idx] || '',
          submission_time: new Date().toISOString(),
          remaining_time: timer,
          user_id: user.id,
          set_id: problem.set_id,
          question_id: problem.id,
          user_email: user.email || null,
          set_title: `${problem.year} ${monthAbbr(problem.month)} ${problem.subject}`,
          exam_year: problem.year,
          exam_month: problem.month.toString(),
          evaluated_at: null,
          score: null,
          evaluation_status: selectedMeeTicketType.includes('AI') ? '완료' : '대기중',
          evaluator_comments: null,
          evaluation_pdf_url: null,
          is_ai_answer: false,
          is_sample_answer: false,
          is_candidate_answer: true,
          evaluation_type: selectedMeeTicketType.includes('AI') ? 'ai' : 'professor'
        }));

        console.log('insertPayloads:', insertPayloads);

        const { error, data } = await supabase.from('mee_answers').insert(insertPayloads);

        if (error) {
          console.error('insert error:', error);
          throw new Error('답안 저장에 실패했습니다: ' + error.message);
        }
        console.log('inserted data:', data);

        await supabase.from('user_exam_tickets')
          .update({ count: ticket.count - 1 })
          .eq('user_id', user.id)
          .eq('type', selectedMeeTicketType);

        alert('평가가 종료되었습니다. 응시권이 1개 차감되었습니다.');
        setIsSubmitted(true);
      });

    } catch (error) {
      console.error('답안 제출 오류:', error);
      if (error.message?.includes('인증이 필요합니다')) {
        alert('로그인이 만료되었습니다. 페이지를 새로고침하고 다시 로그인해주세요.');
      } else {
        alert('답안 제출 중 오류가 발생했습니다: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 문제 데이터가 바뀔 때 activeIdx를 0으로 리셋
  useEffect(() => {
    setActiveIdx(0);
  }, [problems.length]);

  // handleSubmit 함수도 권종별로 분기 (MPT 기준)
  const handleSubmit = async () => {
    if (!problems.length || isSubmitted || isSubmitting || answers.every(a => !a.trim())) return;
    
    try {
      setIsSubmitting(true);

      await withAuthRefresh(async (user) => {
        const ticket = ticketList.find(t => t.type === selectedTicketType);
        if (!ticket || ticket.count < 1) {
          throw new Error('선택한 권종이 없습니다. 구매 후 이용해 주세요.');
        }

        if (selectedTicketType === 'STUDY_MEE_1') {
          // 1문제 셀프 제출, 평가 의뢰 불가
          if (!answers[activeIdx] || !answers[activeIdx].trim()) {
            throw new Error('답안을 입력하세요.');
          }
          
          await supabase.from('user_study_tickets')
            .update({ count: ticket.count - 1 })
            .eq('user_id', user.id)
            .eq('type', 'STUDY_MEE_1');
          
          // mee_answers에도 답안 저장
          const answerValue = answers[activeIdx] || '';
          const setIdValue = problems[activeIdx]?.id || null;
          
          const { error } = await supabase.from('mee_answers').insert([{
            submission_group_id: uuidv4(),
            subject: problems[activeIdx].subject,
            answer_content: answerValue,
            submission_time: new Date().toISOString(),
            remaining_time: timer,
            user_id: user.id,
            set_id: setIdValue,
            user_email: user.email || null,
            set_title: `${problems[activeIdx].year} ${monthAbbr(problems[activeIdx].month)} ${problems[activeIdx].subject}`,
            exam_year: problems[activeIdx].year,
            exam_month: problems[activeIdx].month.toString(),
            evaluated_at: null,
            score: null,
            evaluation_status: '대기중',
            evaluator_comments: null,
            evaluation_pdf_url: null,
            is_ai_answer: false,
            is_sample_answer: false,
            is_candidate_answer: true,
            evaluation_type: 'self',
            question_id: problems[activeIdx].id
          }]);
          
          if (error) {
            throw new Error('답안 저장에 실패했습니다: ' + error.message);
          }
          
          alert('셀프 스터디 제출 완료! 다양한 답안 비교가 가능합니다.');
          setIsSubmitted(true);
        } else {
          // AI/Professor 응시권: 6문제 모두 입력해야 제출 가능
          if (answers.length !== 6 || answers.some(a => !a.trim())) {
            throw new Error('6문제 모두 입력해야 제출할 수 있습니다.');
          }
          
          // 6문제(또는 여러 문제) 답안 저장
          const submissionGroupId = uuidv4();

          const answersToSubmit = answers.map((answer, i) => ({
            user_id: user.id,
            answer_content: answer || '',
            subject: problems[i].subject,
            year: selectedYear,
            month: selectedMonth,
            set_id: problems[i].id,
            question_id: problems[i].id,
            submission_group_id: submissionGroupId,
            evaluation_type: selectedTicketType.includes('AI') ? 'ai' : 'professor',
            evaluation_status: selectedTicketType.includes('AI') ? '완료' : '대기중',
          }));

          const { error } = await supabase.from('mee_answers').insert(answersToSubmit);
          if (error) {
            throw new Error('답안 저장에 실패했습니다: ' + error.message);
          }
          
          // 응시권 차감
          await supabase.from('user_exam_tickets')
            .update({ count: ticket.count - 1 })
            .eq('user_id', user.id)
            .eq('type', selectedTicketType);
          
          alert('평가가 종료되었습니다. 응시권이 1개 차감되었습니다.');
          setIsSubmitted(true);
        }
      });

    } catch (error) {
      console.error('답안 제출 오류:', error);
      if (error.message?.includes('인증이 필요합니다')) {
        alert('로그인이 만료되었습니다. 페이지를 새로고침하고 다시 로그인해주세요.');
      } else {
        alert('답안 제출 중 오류가 발생했습니다: ' + error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // 문제 세트/과목/년도/월/권종이 변경될 때 selectedTicketType 자동 재설정
  useEffect(() => {
    if (ticketList.length > 0) {
      setSelectedTicketType(ticketList[0].type);
    } else {
      setSelectedTicketType('');
    }
  }, [problems, ticketList, selectedSubject, selectedYear, selectedMonth]);

  // Dialog 오픈/닫기 핸들러
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  // 실제 제출 함수(확인 후 실행)
  const handleConfirmSubmit = async () => {
    setDialogOpen(false);
    await handleSubmit();
  };

  const hasAnyTicket = ticketList.some(ticket => ticket.count > 0);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 3
      }}
    >
      <Container maxWidth="xl">
                {/* 헤더 섹션 */}
        <Fade in timeout={800}>
          <Box sx={{ 
            textAlign: 'center', 
            mb: 6,
            py: 4,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255, 255, 255, 0.8)'
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h2" sx={{ 
                fontWeight: 800, 
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2, #9c27b0)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                📚 MEE 실전 테스트
              </Typography>
              <Typography variant="h5" sx={{ 
                mb: 3, 
                fontWeight: 400,
                color: '#424242'
              }}>
                Multistate Essay Examination • {sets.length > 0 && `총 ${sets.length}개 세트 제공`}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                <Chip 
                  icon={<SchoolIcon />} 
                  label="에세이 시험" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
                <Chip 
                  icon={<EditIcon />} 
                  label="논리적 서술" 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="전문가 평가" 
                  color="success" 
                  variant="outlined"
                  sx={{ fontSize: '1rem', py: 1 }}
                />
              </Box>
            </Box>
            
            {/* 타이머 */}
            {timerActive && (
              <Box sx={{ 
                position: 'absolute', 
                top: 16, 
                right: 16,
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                px: 3,
                py: 1.5,
                borderRadius: 3,
                background: timer < 600 ? 
                  'linear-gradient(45deg, #f44336 30%, #ff5722 90%)' :
                  timer < 1800 ? 
                  'linear-gradient(45deg, #ff9800 30%, #ffc107 90%)' :
                  'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)',
                color: 'white',
                boxShadow: 3,
                animation: timer < 300 ? 'pulse 1s infinite' : 'none'
              }}>
                <TimerIcon />
                <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                  {formatTime(timer)}
                </Typography>
              </Box>
            )}

            {/* 진행률 표시 */}
            {problems.length > 0 && (
              <Box sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#424242' }}>
                    선택된 문제: {problems.length}개
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#424242' }}>
                    {selectedSubject && `과목: ${selectedSubject}`}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={(Object.keys(answers).length / problems.length) * 100} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                      borderRadius: 4
                    }
                  }} 
                />
              </Box>
            )}
          </Box>
        </Fade>

        {/* 선택 컨트롤 카드 */}
        <Fade in timeout={600}>
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                시험 문제 선택
              </Typography>
              
              {/* 과목별/년월별 선택 */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SchoolIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                      과목별 연습
                    </Typography>
                    <FormControl sx={{ minWidth: 150 }}>
                      <InputLabel>과목</InputLabel>
                      <Select 
                        value={selectedSubject} 
                        label="과목" 
                        onChange={handleSubjectChange}
                        sx={{ borderRadius: 2 }}
                      >
                        {subjectList.map(subject => (
                          <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CalendarIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                      년월별 선택
                    </Typography>
                    <FormControl sx={{ minWidth: 100 }}>
                      <InputLabel>년도</InputLabel>
                      <Select 
                        value={selectedYear} 
                        label="년도" 
                        onChange={handleYearChange}
                        sx={{ borderRadius: 2 }}
                      >
                        {years.map(year => (
                          <MenuItem key={year} value={year}>{year}년</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 80 }}>
                      <InputLabel>월</InputLabel>
                      <Select 
                        value={selectedMonth} 
                        label="월" 
                        onChange={handleMonthChange}
                        sx={{ borderRadius: 2 }}
                      >
                        {months.map(month => (
                          <MenuItem key={month} value={month}>{month}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>

              {/* 응시권 선택 */}
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                🎫 응시권 선택
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel>응시권 선택</InputLabel>
                  <Select
                    value={selectedTicketType}
                    label="응시권 선택"
                    onChange={e => setSelectedTicketType(e.target.value)}
                    disabled={ticketList.length === 0}
                    sx={{ borderRadius: 2 }}
                  >
                    {ticketList.map(ticket => (
                      <MenuItem key={ticket.type} value={ticket.type}>
                        {ticketTypeLabels[ticket.type] || ticket.type} ({ticket.count}개)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {ticketList.map(ticket => (
                  <Chip
                    key={ticket.type}
                    icon={<span role="img" aria-label="ticket">🎫</span>}
                    label={`${ticketTypeLabels[ticket.type] || ticket.type} : ${ticket.count}개`}
                    color="primary"
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                ))}
                {ticketList.length === 0 && (
                  <Chip
                    icon={<span role="img" aria-label="ticket">🎫</span>}
                    label="보유한 MEE 응시권이 없습니다."
                    color="default"
                    sx={{ borderRadius: 2 }}
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Fade>

      {/* 4. 문제/답안 입력 전체 레이아웃 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '6fr 4fr',
          gap: { xs: 2, sm: 3 },
          height: 'calc(100vh - 380px)',
          alignItems: 'stretch',
          mb: 3,
          minWidth: 0,
          minHeight: 0,
        }}
      >
        {/* PDF 뷰어 영역 */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'visible' }}>
          <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {problems.length === 0 ? (
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                minHeight: 400,
                flexDirection: 'column',
                gap: 2
              }}>
                <DescriptionIcon sx={{ fontSize: 64, opacity: 0.3, color: 'primary.main' }} />
                <Typography variant="h6" color="text.secondary">
                  문제 PDF를 선택하세요
                </Typography>
              </Box>
            ) : (
              <>
                <Tabs
                  value={activeIdx}
                  onChange={(_, v) => setActiveIdx(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    '& .MuiTab-root': {
                      minHeight: 40,
                      py: 1,
                      px: 3,
                      borderRadius: 2,
                      mx: 0.5,
                      fontWeight: 'bold',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.main',
                        color: 'white'
                      }
                    },
                    '& .MuiTabs-indicator': {
                      display: 'none'
                    }
                  }}
                >
                  {problems.map((p, idx) => (
                    <Tab
                      key={p.id}
                      value={idx}
                      label={
                        selectedSubject
                          ? `${p.year} ${monthAbbr(p.month)}`
                          : (
                            <Box display="flex" alignItems="center" gap={1}>
                              <span role="img" aria-label="답안">✏️</span>
                              {p.subject}
                            </Box>
                          )
                      }
                    />
                  ))}
                </Tabs>
                <Box sx={{ 
                  flex: 1, 
                  p: 0,
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'visible'
                }}>
                  <PdfViewer url={problems[activeIdx]?.q_url} label={problems[activeIdx]?.subject} />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
        {/* 답안 입력 영역 */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
          <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {problems.length === 0 ? (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#3b3b3b', flexShrink: 0 }}>
                답안 입력
              </Typography>
              <RichTextEditor
                value={''}
                onChange={() => {}}
                disabled={true}
                placeholder="시험 세트를 먼저 선택하세요"
              />
              <Button 
                variant="contained" 
                color="primary" 
                size="small" 
                sx={{ fontWeight: 600, borderRadius: 2, flexShrink: 0, py: 1, mt: 1, bgcolor: '#e0e0e0', color: '#9e9e9e', '&:hover': { bgcolor: '#e0e0e0' } }} 
                fullWidth 
                disabled
              >
                응시권 필요
              </Button>
              {problems.length === 0 && !hasAnyTicket && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    MEE 응시권 또는 셀프 MEE 스터디권이 필요합니다.{' '}
                    <Link
                      component={RouterLink}
                      to="/pricing"
                      sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      요금제 페이지로 이동
                    </Link>
                  </Typography>
                </Alert>
              )}
            </>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: '#3b3b3b', flexShrink: 0 }}>
                답안 입력 {isSubmitted && (<Typography component="span" sx={{ ml: 2, color: 'success.main', fontSize: '0.9rem' }}>(제출 완료)</Typography>)}
              </Typography>
              <Tabs
                value={activeIdx}
                onChange={(_, v) => setActiveIdx(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  px: 1,
                  minHeight: 36,
                  borderBottom: 1,
                  borderColor: 'divider',
                  flexShrink: 0,
                  '& .MuiTab-root': {
                    minHeight: 36,
                    py: 0.5,
                    px: 2,
                    mx: 0.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    background: '#f0f4fa',
                    color: '#4B48DF',
                    border: '1px solid #d0d7e2',
                    transition: 'background 0.2s, color 0.2s',
                    '&.Mui-selected': {
                      background: '#4B48DF',
                      color: '#fff',
                      border: '1.5px solid #4B48DF',
                      boxShadow: '0 2px 8px 0 rgba(60,60,60,0.07)',
                    },
                    '&:hover': {
                      background: '#e3e8f7',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                  '& .MuiTabs-scrollButtons': {
                    color: '#4B48DF',
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                }}
              >
                {problems.map((p, idx) => (
                  <Tab
                    key={p.id}
                    value={idx}
                    label={
                      selectedSubject
                        ? `${p.year} ${monthAbbr(p.month)}`
                        : (
                          <Box display="flex" alignItems="center" gap={1}>
                            <span role="img" aria-label="답안">✏️</span>
                            {p.subject}
                          </Box>
                        )
                    }
                  />
                ))}
              </Tabs>
              <Box sx={{ flex: 1, overflow: 'hidden',
                '& .ProseMirror': {
                  height: '100%',
                  minHeight: 120,
                  maxHeight: '100%',
                  overflow: 'auto',
                  outline: 'none',
                }
              }}>
                <RichTextEditor
                  value={answers[activeIdx] || ''}
                  onChange={handleAnswerChange(activeIdx)}
                  disabled={isSubmitted}
                  placeholder={isSubmitted ? "평가가 종료되었습니다. 일주일 내에 이메일로 결과를 발송해 드립니다." : problems[activeIdx] ? `${problems[activeIdx].subject} 답안을 입력하세요` : "시험 세트를 먼저 선택하세요"}
                />
              </Box>
              <Button 
                variant="contained" 
                color="primary" 
                size="small" 
                sx={{ 
                  fontWeight: 600, 
                  borderRadius: 2, 
                  flexShrink: 0, 
                  py: 1, 
                  mt: 1,
                  ...(!selectedTicketType && {
                    bgcolor: '#e0e0e0',
                    color: '#9e9e9e',
                    '&:hover': {
                      bgcolor: '#e0e0e0',
                    }
                  })
                }} 
                onClick={handleOpenDialog} 
                fullWidth 
                disabled={
                  !problems.length || 
                  isSubmitted || 
                  isSubmitting || 
                  answers.every(a => !a.trim()) || 
                  !selectedTicketType ||
                  (ticketList.find(t => t.type === selectedTicketType)?.count < 1)
                }
              >
                {!selectedTicketType
                  ? '응시권 필요'
                  : isSubmitting
                  ? '제출 중...'
                  : isSubmitted
                  ? '제출 완료'
                  : '답안 제출'}
              </Button>
              {problems.length === 0 && !hasAnyTicket && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    MEE 응시권 또는 셀프 MEE 스터디권이 필요합니다.{' '}
                    <Link
                      component={RouterLink}
                      to="/pricing"
                      sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      요금제 페이지로 이동
                    </Link>
                  </Typography>
                </Alert>
              )}
              {isSubmitting && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: 0, 
                  left: 0, 
                  right: 0, 
                  bottom: 0, 
                  background: 'rgba(255, 255, 255, 0.7)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  zIndex: 1 
                }}>
                  <Typography sx={{ color: 'primary.main', fontWeight: 600 }}>
                    답안을 제출하는 중입니다...
                  </Typography>
                </Box>
              )}
            </>
          )}
          </CardContent>
        </Card>
      </Box>
      {!selectedTicketType && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          <Typography variant="body2">
            MEE 응시권이 필요합니다.{' '}
            <Link
              component={RouterLink}
              to="/pricing"
              sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
            >
              요금제 페이지로 이동
            </Link>
          </Typography>
        </Alert>
      )}
      {/* 답안 제출 확인 Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            boxShadow: 8,
            minWidth: 340,
            p: 1.5,
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.light',
            color: 'primary.main',
            fontWeight: 700,
            textAlign: 'center',
            fontSize: '1.25rem',
            letterSpacing: 1,
            borderRadius: 2,
            mb: 1
          }}
        >
          답안 제출 확인
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary', mb: 2 }}>
            {selectedTicketType === 'STUDY_MEE_1' ? (
              <>
                <span style={{ fontWeight: 700, color: '#1976d2' }}>
                  {problems[activeIdx]?.year} {monthAbbr(problems[activeIdx]?.month)} {problems[activeIdx]?.subject}
                </span>
                <br />
                <span style={{ color: '#666', fontSize: '0.98rem' }}>
                  셀프 스터디권 1회가 차감됩니다.<br />
                  이 문제의 답안만 제출됩니다.<br />
                  제출하시겠습니까?
                </span>
              </>
            ) : (
              <>
                <span style={{ fontWeight: 700, color: '#1976d2' }}>
                  {problems[activeIdx]?.year} {monthAbbr(problems[activeIdx]?.month)} {problems[activeIdx]?.subject}
                </span>
                <br />
                <span style={{ color: '#666', fontSize: '0.98rem' }}>
                  MEE 응시권 1회가 차감됩니다.<br />
                  6개 답안이 한 번에 제출됩니다.<br />
                  <b>6개 답안을 모두 작성하셨나요?</b>
                </span>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button 
            onClick={handleCloseDialog} 
            color="inherit" 
            variant="outlined" 
            sx={{ minWidth: 90, borderRadius: 2 }}
          >
            취소
          </Button>
          <Button 
            onClick={handleConfirmSubmit} 
            color="primary" 
            variant="contained" 
            sx={{ minWidth: 90, borderRadius: 2, fontWeight: 700 }}
          >
            제출
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default MeeTestMainPage;